import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import {
  IconLoader, IconAlert,
} from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { useCustomPaymentMethods, customPaymentName } from "../hooks/useCustomPaymentMethods";

interface Branch { id: string; name: string; }

interface SalesReport {
  total_revenue_minor: number;
  order_count: number;
  avg_order_minor: number;
  currency: string;
  daily_breakdown: { date: string; revenue: number; count: number }[];
  by_branch: { branch_name: string; revenue: number; count: number }[];
}
interface TopProduct {
  product_name: string;
  quantity_sold: number;
  revenue_minor: number;
  order_count: number;
}
interface PaymentBreakdown {
  method: string;
  provider: string | null;
  total_minor: number;
  count: number;
  percentage: number;
}
interface VatBreakdown {
  vat_rate: number;
  base_minor: number;
  vat_minor: number;
  gross_minor: number;
}
interface EmployeeHours {
  employee_name: string;
  branch_name: string;
  total_hours: number;
  shift_count: number;
}

function formatMoney(minor: number, currency = "CZK"): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function defaultDateFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return isoDate(d);
}
function defaultDateTo(): string {
  return isoDate(new Date());
}

type PeriodPreset = "last7" | "last30" | "lastWeek" | "lastMonth" | "thisWeek" | "thisMonth" | "custom";

const PERIOD_KEYS: PeriodPreset[] = ["last7", "last30", "lastWeek", "lastMonth", "thisWeek", "thisMonth", "custom"];

function periodRange(preset: Exclude<PeriodPreset, "custom">): [string, string] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "last7": {
      const from = new Date(today); from.setDate(from.getDate() - 6);
      return [isoDate(from), isoDate(today)];
    }
    case "last30": {
      const from = new Date(today); from.setDate(from.getDate() - 29);
      return [isoDate(from), isoDate(today)];
    }
    case "thisWeek": {
      const dow = (today.getDay() + 6) % 7; // Monday = 0
      const from = new Date(today); from.setDate(from.getDate() - dow);
      return [isoDate(from), isoDate(today)];
    }
    case "lastWeek": {
      const dow = (today.getDay() + 6) % 7;
      const monday = new Date(today); monday.setDate(monday.getDate() - dow - 7);
      const sunday = new Date(monday); sunday.setDate(sunday.getDate() + 6);
      return [isoDate(monday), isoDate(sunday)];
    }
    case "thisMonth": {
      return [isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), isoDate(today)];
    }
    case "lastMonth": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return [isoDate(first), isoDate(last)];
    }
  }
}

type ReportTab = "sales" | "products" | "payments" | "vat" | "employees";

export default function Reports() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canRevenue = can(user, "revenue_overview");
  const canAttendance = can(user, "attendance");
  const [tab, setTab] = useState<ReportTab>(canRevenue ? "sales" : "employees");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [period, setPeriod] = useState<PeriodPreset>("last7");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [branchId, setBranchId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [sales, setSales] = useState<SalesReport | null>(null);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [payments, setPayments] = useState<PaymentBreakdown[]>([]);
  const [vatData, setVatData] = useState<VatBreakdown[]>([]);
  const [empHours, setEmpHours] = useState<EmployeeHours[]>([]);
  const [isVatPayer, setIsVatPayer] = useState(true);
  // Persistent net/gross view toggle for VAT payers (sales tab).
  const [netView, setNetView] = useState(() => localStorage.getItem("reports.netView") === "1");
  const toggleNetView = () => setNetView(v => { localStorage.setItem("reports.netView", v ? "0" : "1"); return !v; });

  useEffect(() => {
    api.get<Branch[]>("/branches?limit=500").then(r => setBranches(r.data)).catch(() => {});
    api.get<{ is_vat_payer: boolean }>("/tenant/profile")
      .then(r => setIsVatPayer(r.data.is_vat_payer))
      .catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (branchId !== "all") p.set("branch_id", branchId);
    return p;
  }, [dateFrom, dateTo, branchId]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const p = params();
      if (tab === "sales") {
        if (netView) p.set("net", "true");
        const { data } = await api.get<SalesReport>(`/reports/sales?${p}`);
        setSales(data);
      } else if (tab === "products") {
        const { data } = await api.get<TopProduct[]>(`/reports/top-products?${p}&limit=20`);
        setTopProducts(data);
      } else if (tab === "payments") {
        const { data } = await api.get<PaymentBreakdown[]>(`/reports/payments?${p}`);
        setPayments(data);
      } else if (tab === "vat") {
        const { data } = await api.get<VatBreakdown[]>(`/reports/vat?${p}`);
        setVatData(data);
      } else if (tab === "employees") {
        const { data } = await api.get<EmployeeHours[]>(`/reports/employee-hours?${p}`);
        setEmpHours(data);
      }
    } catch (e) {
      setErr(apiError(e, t("reports.loadError")));
    } finally {
      setLoading(false);
    }
  }, [tab, params, netView, t]);

  const customMethods = useCustomPaymentMethods();
  const paymentLabel = (p: { method: string; provider?: string | null }) => {
    const key = p.method === "card" && p.provider === "sumup"
      ? "sumup"
      : p.provider === "meal_voucher" ? "meal_voucher" : p.method === "other" && p.provider ? customPaymentName(customMethods, p.provider) : p.method;
    return t(`orders.pay.${key}`, { defaultValue: key });
  };

  // Auto-reload on filter changes — except for a custom period, where the
  // user edits Od/Do and confirms with the "Načíst" button.
  useEffect(() => { if (period !== "custom") void loadReport(); }, [loadReport, period]);

  const tabs: { id: ReportTab; label: string }[] = [
    ...(canRevenue ? [
      { id: "sales" as ReportTab, label: t("reports.tabSales") },
      { id: "products" as ReportTab, label: t("reports.tabProducts") },
      { id: "payments" as ReportTab, label: t("reports.tabPayments") },
      ...(isVatPayer ? [{ id: "vat" as ReportTab, label: "DPH" }] : []),
    ] : []),
    ...(canAttendance ? [{ id: "employees" as ReportTab, label: t("reports.tabEmployees") }] : []),
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t("reports.title")}</h1>
        <p>{t("reports.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ padding: 16, marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>{t("reports.period")}</label>
          <select
            value={period}
            onChange={e => {
              const p = e.target.value as PeriodPreset;
              setPeriod(p);
              if (p !== "custom") {
                const [from, to] = periodRange(p);
                setDateFrom(from);
                setDateTo(to);
              }
            }}
            style={{ fontSize: 14 }}
          >
            {PERIOD_KEYS.map(k => (
              <option key={k} value={k}>{t(`reports.periods.${k}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>{t("reports.dateFrom")}</label>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriod("custom"); }} style={{ fontSize: 14 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>{t("reports.dateTo")}</label>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriod("custom"); }} style={{ fontSize: 14 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--muted)" }}>{t("orders.colBranch")}</label>
          <SearchableSelect
            value={branchId}
            onChange={setBranchId}
            options={[{ value: "all", label: t("reports.all") }, ...branches.map(b => ({ value: b.id, label: b.name }))]}
            placeholder={t("reports.all")}
            searchPlaceholder={t("dashboard.searchBranch")}
          />
        </div>
        <button className="primary" onClick={loadReport}>{t("reports.load")}</button>
        {isVatPayer && tab === "sales" && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "var(--muted)" }}>{t("reports.vatView")}</label>
            <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              <button onClick={() => { if (netView) toggleNetView(); }} style={{
                padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer",
                background: !netView ? "var(--accent)" : "transparent",
                color: !netView ? "#fff" : "var(--muted)", fontWeight: !netView ? 600 : 400,
              }}>{t("reports.vatGross")}</button>
              <button onClick={() => { if (!netView) toggleNetView(); }} style={{
                padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer",
                background: netView ? "var(--accent)" : "transparent",
                color: netView ? "#fff" : "var(--muted)", fontWeight: netView ? 600 : 400,
              }}>{t("reports.vatNet")}</button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{
            padding: "10px 18px", border: "none", background: "transparent",
            color: tab === tb.id ? "var(--accent)" : "var(--muted)",
            fontWeight: tab === tb.id ? 600 : 500, fontSize: 14,
            borderBottom: tab === tb.id ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer", marginBottom: -1,
          }}>{tb.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IconLoader size={32} className="muted" /></div>
      ) : (
        <>
          {/* Sales */}
          {tab === "sales" && sales && (
            <>
              <div className="grid grid-3" style={{ marginBottom: 24 }}>
                <StatCard label={t("reports.revenueTotal")} value={formatMoney(sales.total_revenue_minor, sales.currency)} />
                <StatCard label={t("reports.orderCount")} value={String(sales.order_count)} />
                <StatCard label={t("reports.avgOrder")} value={formatMoney(sales.avg_order_minor, sales.currency)} />
              </div>
              {sales.by_branch.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("reports.byBranch")}</h3>
                  <table>
                    <thead><tr><th>{t("orders.colBranch")}</th><th>{t("reports.revenue")}</th><th>{t("reports.orderCount")}</th></tr></thead>
                    <tbody>
                      {sales.by_branch.map((b, i) => (
                        <tr key={i}><td style={{ fontWeight: 600 }}>{b.branch_name}</td><td style={{ fontWeight: 600 }}>{formatMoney(b.revenue, sales.currency)}</td><td>{b.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(() => {
                const byDate = new Map(sales.daily_breakdown.map(d => [d.date, d]));
                const days: { date: string; revenue: number; count: number }[] = [];
                const cur = new Date(dateFrom + "T00:00:00");
                const end = new Date(dateTo + "T00:00:00");
                while (cur <= end && days.length < 62) {
                  const key = isoDate(cur);
                  days.push(byDate.get(key) ?? { date: key, revenue: 0, count: 0 });
                  cur.setDate(cur.getDate() + 1);
                }
                if (days.length === 0) return null;
                const max = Math.max(...days.map(x => x.revenue), 1);
                const barCap = days.length <= 7 ? 72 : days.length <= 14 ? 48 : days.length <= 21 ? 40 : 32;
                return (
                  <div className="card">
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("reports.dailyRevenue")}</h3>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 160, paddingTop: 10 }}>
                      {days.map((d) => {
                        const [, m, day] = d.date.split("-");
                        return (
                          <div key={d.date} style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 4, height: "100%" }}>
                            <div title={`${formatMoney(d.revenue, sales.currency)} · ${t("reports.ordersTooltip", { count: d.count })}`} style={{
                              width: "100%", maxWidth: barCap, borderRadius: "4px 4px 0 0",
                              height: `${Math.max(2, (d.revenue / max) * 100)}%`,
                              background: "var(--gradient-sunset)", opacity: d.revenue > 0 ? 0.85 : 0.25,
                            }} />
                            <span style={{ fontSize: 9, color: "var(--muted)" }}>{`${day}.${m}.`}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* Top products */}
          {tab === "products" && (
            <div className="card">
              {topProducts.length === 0 ? (
                <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("dashboard.noData")}</div>
              ) : (
                <table>
                  <thead><tr><th>#</th><th>{t("reports.colProduct")}</th><th>{t("reports.colSold")}</th><th>{t("reports.revenue")}</th><th>{t("reports.orderCount")}</th></tr></thead>
                  <tbody>
                    {topProducts.map((p, i) => (
                      <tr key={i}>
                        <td style={{ color: "var(--muted)" }}>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{p.product_name}</td>
                        <td>{p.quantity_sold}</td>
                        <td style={{ fontWeight: 600 }}>{formatMoney(p.revenue_minor)}</td>
                        <td>{p.order_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Payments */}
          {tab === "payments" && (
            <div className="card">
              {payments.length === 0 ? (
                <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("dashboard.noData")}</div>
              ) : (
                <table>
                  <thead><tr><th>{t("reports.colMethod")}</th><th>{t("cashOps.colAmount")}</th><th>{t("reports.colCount")}</th><th>{t("reports.colShare")}</th></tr></thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{paymentLabel(p)}</td>
                        <td style={{ fontWeight: 600 }}>{formatMoney(p.total_minor)}</td>
                        <td>{p.count}</td>
                        <td><span className="badge badge-green">{p.percentage}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* VAT */}
          {tab === "vat" && (
            <div className="card">
              {!isVatPayer ? (
                <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("reports.notVatPayer")}</div>
              ) : vatData.length === 0 ? (
                <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("dashboard.noData")}</div>
              ) : (
                <table>
                  <thead><tr><th>{t("reports.colRate")}</th><th>{t("reports.colBase")}</th><th>DPH</th><th>{t("reports.colGross")}</th></tr></thead>
                  <tbody>
                    {vatData.map((v, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{v.vat_rate}%</td>
                        <td>{formatMoney(v.base_minor)}</td>
                        <td>{formatMoney(v.vat_minor)}</td>
                        <td style={{ fontWeight: 600 }}>{formatMoney(v.gross_minor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Employees */}
          {tab === "employees" && (
            <div className="card">
              {empHours.length === 0 ? (
                <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("dashboard.noData")}</div>
              ) : (
                <table>
                  <thead><tr><th>{t("cashOps.colEmployee")}</th><th>{t("orders.colBranch")}</th><th>{t("reports.colHours")}</th><th>{t("reports.colShifts")}</th></tr></thead>
                  <tbody>
                    {empHours.map((e, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{e.employee_name}</td>
                        <td>{e.branch_name}</td>
                        <td><span className="badge badge-green">{e.total_hours.toFixed(1)} h</span></td>
                        <td>{e.shift_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>{value}</div>
      <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{label}</div>
    </div>
  );
}
